import type { Tone } from '../../components/StatusBadge';
import type { AuthedRequest } from '../auth/AuthProvider';
import type { RecordingErrorCode } from '../recording/api';
import type { TenantRef } from './media';

/** Mirrors GET /api/v1/system/recording (system.read: every tenant for the super administrator). */
export interface RecordingHealth {
  /** False when this deployment has no recording storage. */
  available: boolean;
  cameras: {
    recording: number;
    failing: number;
    starting: number;
    /**
     * Not recording: switched off, no policy, or paused (disabled device, or
     * suspended account, client or tenant).
     */
    disabled: number;
    /** Scheduled cameras outside their windows (absent before 0.38). */
    off_schedule?: number;
    /** Event-mode cameras outside an event window (absent before 0.64). */
    waiting_event?: number;
  };
  failures: {
    /** Whose camera it is (0.67.0: every tenant's, for the super administrator). */
    tenant?: TenantRef | null;
    device: { id: string; name: string; site: string };
    code: RecordingErrorCode;
    since: string | null;
  }[];
  storage: {
    segments: number;
    bytes: number;
    last_hour_bytes: number;
    oldest_recording: string | null;
    newest_recording: string | null;
    /** Where new segments go (ADR-035); absent before 0.39. */
    provider?: 'local' | 's3' | '';
  };
  /** The node's recording volume, shared by every organisation; null if unknown. */
  disk: { total_bytes: number; free_bytes: number } | null;
  checked_at: string;
}

export const recordingHealthKey = ['system', 'recording'] as const;

export const getRecordingHealth = (req: AuthedRequest) =>
  req<RecordingHealth>('GET', '/api/v1/system/recording');

/** Disk tone by used share: warn from 80 %, critical from 90 %. */
export function diskTone(total: number, free: number): Tone {
  if (total <= 0) return 'neutral';
  const used = 1 - free / total;
  if (used >= 0.9) return 'crit';
  if (used >= 0.8) return 'warn';
  return 'ok';
}

/**
 * Days until the free space is gone at a given write rate (bytes/hour), or
 * null when nothing is being written.
 */
export function daysUntilFull(freeBytes: number, bytesPerHour: number): number | null {
  if (bytesPerHour <= 0) return null;
  return freeBytes / bytesPerHour / 24;
}
